/**
 * createBasics.content.js — every word and every pre-filled value.
 *
 * Kept apart from the steps so that rewording the tutorial never risks breaking
 * its logic, and so a non-engineer can edit the copy without reading a state
 * machine. `createBasics.steps.js` references `content.steps.<key>` and nothing
 * else.
 */

const content = {
    /** The scenario the tutorial builds on the user's behalf. */
    scenario: {
        title: 'Tutorial Scenario',
        tag: 'tutorial',
        descriptionHtml:
            '<p>This is a practice scenario created by the beginner tutorial.</p>' +
            '<p>It is a very short branching story: a traveller reaches a fork in the road, ' +
            'crosses an old bridge, and ends up either at a river or in a forest. Everything ' +
            'in it was filled in for you so you could concentrate on how the editor works.</p>' +
            '<p>It is hidden from the public. Edit it, keep it, or delete it — it is yours.</p>',
    },

    /** Pre-filled situation content, applied the moment a node is dropped. */
    nodes: {
        start: {
            title: 'Opening: the fork in the road',
            text:
                '<p>The road splits in two beneath a grey sky. To the left, a narrow track ' +
                'disappears into the trees. To the right, an old stone bridge crosses a river ' +
                'you cannot see the bottom of.</p><p>You have to choose.</p>',
        },
        normal: {
            title: 'The old bridge',
            text:
                '<p>The bridge groans under your boots. Halfway across you stop and look down ' +
                'at the water moving fast and dark below.</p><p>On the far bank the path ' +
                'divides again: one way follows the river, the other climbs into the forest.</p>',
        },
        end1: {
            title: 'Ending: downstream',
            text:
                '<p>You follow the river until the trees thin out and a village appears on the ' +
                'bank, chimneys smoking.</p><p><strong>You have reached the first ending.</strong></p>',
        },
        end2: {
            title: 'Ending: into the forest',
            text:
                '<p>The forest closes over the path. Hours later you come out on a ridge with ' +
                'the whole valley laid out beneath you.</p>' +
                '<p><strong>You have reached the second ending.</strong></p>',
        },
        isolated: {
            title: 'A scene with no way in',
            text:
                '<p>Nothing points at this situation, so nobody reading the scenario could ever ' +
                'arrive here. That makes the scenario invalid. We are about to prove it.</p>',
        },
    },

    /** Pre-filled choice content. `text` is what the reader sees on the bubble. */
    choices: {
        startToNormal: { title: 'to the bridge', text: '<p>Take the bridge.</p>' },
        normalToEnd1: { title: 'to the river ending', text: '<p>Follow the river downstream.</p>' },
        normalToEnd2: { title: 'to the forest ending', text: '<p>Climb into the forest.</p>' },
    },

    /** Corrections, shown under the step body when the user does something else. */
    hints: {
        wrongTile: (kind) =>
            `That was not the ${kind} tile, so I removed it. Find the ${kind} tile in the panel on the left and drag that one onto the canvas.`,
        wrongEdge:
            'That connection joined the wrong two situations, so I removed it. Drag from the small circle on the right edge of the highlighted situation onto the other highlighted situation.',
        publishFailed:
            'Publishing did not go through. Click Check Validity to see what is wrong, then try Publish again.',
    },

    /** The one-time offer on the Create button. */
    prompt: {
        title: 'New here? Take the tour.',
        body:
            'A short guided walkthrough builds a small branching scenario with you, start to finish: ' +
            'situations, choices, previewing, fixing an error, and publishing. It takes a few minutes ' +
            'and everything is filled in for you.',
        note:
            'The tour creates a real scenario called "Tutorial Scenario" in your account, hidden from ' +
            'the public. It counts toward your scenario limit, and you can delete it afterwards. ' +
            'You can leave the tour at any point.',
        mobileNote:
            'The guided tour is only available on a larger screen for now, because it teaches the ' +
            'drag-and-drop editor. You can carry on and create a scenario as usual.',
        play: 'Play the tutorial',
        skip: 'Skip, just create',
        mobileSkip: 'Create a scenario',
    },

    /** How the tour is offered again, once the one-time prompt is spent. */
    replay: {
        label: 'Replay the tutorial',
        note: 'Builds a fresh practice scenario',
        unavailable: 'Scenario limit reached',
    },

    /** The congratulations at the end. */
    complete: {
        title: 'That is the whole loop.',
        body:
            'You built a branching scenario with a start, a junction and two endings, previewed it ' +
            'the way a reader sees it, found and fixed an error, and published it.',
        note:
            '"Tutorial Scenario" is now published but hidden from the public. It is a normal scenario: ' +
            'open it to keep experimenting, or delete it whenever you like.',
        view: 'See my scenarios',
        close: 'Close',
    },

    /** One entry per step id, in the order the user meets them. */
    steps: {
        createModal: {
            title: 'Start with a title',
            body:
                'Every scenario begins here. The title is filled in for you as "Tutorial Scenario" ' +
                'and locked for the tour. Click Create.',
        },
        titleTitle: {
            title: 'The title page',
            body:
                'You are now on the scenario settings page. Readers see this before they start. ' +
                'The title is the one you just created.',
        },
        titleDescription: {
            title: 'Description',
            body:
                'The description is shown on the title page, before anyone makes their first choice. ' +
                'Use it for the premise, the setting, or instructions. This one has been written for you.',
        },
        titlePassword: {
            title: 'Add a Password',
            body:
                'Turn this on to put a password on the scenario. Anyone without it is stopped at the ' +
                'title page. It is off for the tutorial.',
        },
        titleHidden: {
            title: 'Hide From Public',
            body:
                'A hidden scenario never appears in search or browse. Anyone with the link can still ' +
                'open it. It is on for the tutorial, so your practice scenario stays out of the way.',
        },
        titleLogs: {
            title: 'Create Logs',
            body:
                'With logs on, you can see how other people moved through your scenario: which ' +
                'situations they reached and which choices they made. It is off here.',
        },
        titleMap: {
            title: 'Display Map',
            body:
                'This lets readers open a map of the scenario while they read it, showing the ' +
                'structure as a graph. Leave it off if you would rather the shape stayed a surprise.',
        },
        titleCollaborators: {
            title: 'Collaborators',
            body:
                'You can invite up to two other people to edit a scenario with you, in real time. ' +
                'Adding collaborators is turned off during the tutorial.',
        },
        titleTags: {
            title: 'Tags',
            body:
                'Tags are how people find a scenario when browsing. Up to six. This one has been ' +
                'tagged "tutorial" already.',
        },
        titleNext: {
            title: 'On to the editor',
            body: 'That is the whole settings page. Click Next to open the editor and start building.',
        },
        openCreatePanel: {
            title: 'The editor',
            body:
                'Three parts: the icon bar on the left, the canvas in the middle, and the editing ' +
                'panel on the right. The panel already open is where new situations come from — the ' +
                'shapes icon beside it closes and reopens it whenever you want.',
        },
        dragStart: {
            title: 'Add the starting situation',
            body:
                'A situation is one screen of the story. Drag the highlighted START tile onto the ' +
                'canvas — anywhere will do, the tour arranges the layout for you. Every scenario ' +
                'needs exactly one starting situation: it is where readers begin.',
        },
        editTitle: {
            title: 'The title is for you, not the reader',
            body:
                'This field is a label. It is how you find this situation in your own lists and ' +
                'menus, and nobody reading the scenario ever sees it. Give it whatever helps you ' +
                'keep track. It has been filled in for the tour.',
        },
        editText: {
            title: 'This is what the reader sees',
            body:
                'The editor below is the actual content of the situation: the text, images and ' +
                'media that appear on screen when a reader arrives here. It has been written for you.',
        },
        openPreview1: {
            title: 'See it as a reader',
            body: 'Click the green play button to preview this situation the way a reader sees it.',
        },
        previewText: {
            title: 'The reader’s view',
            body:
                'This is your situation, rendered. No titles, no editor: just the content. Every ' +
                'situation you write ends up looking like this.',
        },
        previewExit1: {
            title: 'Back to the canvas',
            body: 'Click the X in the top right to leave the preview.',
        },
        dragNormal: {
            title: 'Add a second situation',
            body:
                'Now drag the plain situation tile, the top one, onto the canvas. This will be the ' +
                'next beat of the story.',
        },
        connectStartNormal: {
            title: 'Join them with a choice',
            body:
                'Drag from the small circle on the right edge of the starting situation onto the ' +
                'second situation. That line is a choice: an option the reader can click.',
        },
        choiceExplain: {
            title: 'A choice has a label and a text too',
            body:
                'Same split as a situation. The title is your own label for the choice. The text ' +
                'below it is what the reader actually sees on the button they click.',
        },
        selectStart: {
            title: 'Select the starting situation',
            body:
                'Click the starting situation on the canvas to select it. We are going to look at ' +
                'it from the reader’s side again, now that it leads somewhere.',
        },
        openPreview2: {
            title: 'Preview it again',
            body: 'Click the green play button once more.',
        },
        previewChoice: {
            title: 'There is your choice',
            body:
                'The choice appears as a bubble under the situation text. Its wording is the ' +
                'choice’s text, not its title. A situation with three choices shows three ' +
                'bubbles, in the order you set.',
        },
        previewExit2: {
            title: 'Back to the canvas',
            body: 'Close the preview again with the X.',
        },
        dragEnd1: {
            title: 'Add an ending',
            body:
                'Drag the END tile onto the canvas. An ending is where a path stops. A scenario ' +
                'needs at least one, and can have as many as you like.',
        },
        dragEnd2: {
            title: 'And a second ending',
            body:
                'Drag another END tile onto the canvas. Two endings is what makes this a branching ' +
                'story rather than a straight line.',
        },
        connectNormalEnd1: {
            title: 'Branch to the first ending',
            body:
                'Drag from the second situation’s right-hand circle to the first ending. ' +
                'That is one of the two paths.',
        },
        connectNormalEnd2: {
            title: 'Branch to the second ending',
            body:
                'Now drag from the same situation to the other ending. One situation, two choices, ' +
                'two different endings.',
        },
        railPreview: {
            title: 'Read the whole thing',
            body:
                'The eye icon previews the scenario from the very beginning, the way a reader ' +
                'arrives at it. Click it.',
        },
        previewStart: {
            title: 'The title page, as a reader sees it',
            body:
                'Your title and description, then a Start button. Click Start.',
        },
        previewChoice1: {
            title: 'Make a choice',
            body: 'Click the bubble to move to the next situation.',
        },
        previewEnd1: {
            title: 'Take the first branch',
            body: 'Two bubbles now, one per ending. Click the first one.',
        },
        previewRestart: {
            title: 'You reached an ending',
            body:
                'The path stops here: no more bubbles. Click the circular arrow in the top right ' +
                'to start over, so you can try the other branch.',
        },
        previewStart2: {
            title: 'Once more from the top',
            body: 'Click Start again.',
        },
        previewChoice1b: {
            title: 'Same first choice',
            body: 'Click the bubble to cross the bridge again.',
        },
        previewEnd2: {
            title: 'Now the other branch',
            body: 'This time click the second bubble, and you will land on the other ending.',
        },
        previewExit3: {
            title: 'Done reading',
            body: 'Close the preview with the X to get back to the canvas.',
        },
        dragIsolated: {
            title: 'Now let us break it on purpose',
            body:
                'Drag one more plain situation onto the canvas and leave it unconnected. A ' +
                'situation nothing points at is one of the errors that stops a scenario publishing, ' +
                'and it is worth seeing what that looks like.',
        },
        checkValidity1: {
            title: 'Check the scenario',
            body:
                'Click the magnifying glass. It checks the whole scenario for problems that would ' +
                'stop it publishing.',
        },
        clickErrorItem: {
            title: 'There is the problem',
            body:
                'Unreachable node: no choice leads to that situation, so no reader could ever see ' +
                'it. Click the error to jump straight to the situation at fault.',
        },
        openSettingsTab: {
            title: 'Open its settings',
            body:
                'The broken situation is now selected on the right. Click the Settings tab.',
        },
        clickDelete: {
            title: 'Delete it',
            body: 'Click Delete Situation.',
        },
        confirmDelete: {
            title: 'Confirm',
            body: 'Click Yes. Deleting a situation also removes any choices attached to it.',
        },
        checkValidity2: {
            title: 'Check again',
            body: 'Click the magnifying glass once more. This time there should be nothing wrong.',
        },
        publish: {
            title: 'Publish',
            body:
                'A green tick means the scenario is sound. Click the paper plane to publish it. ' +
                'Until you publish, everything you have done stays a private draft.',
        },
    },
};

export default content;
